import React, { useEffect, useRef, useState } from 'react';
import { aboutContent } from '../data/mockData';
import { useTranslation } from 'react-i18next';
import scopeImage from '../assets/about_imag.jpeg';


const useReveal = (delay = 0) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const About = () => {
  const [secRef, secVis] = useReveal(0);
  const [imgRef, imgVis] = useReveal(80);
  const [s1Ref, s1Vis]   = useReveal(0);
  const [s2Ref, s2Vis]   = useReveal(80);
  const [s3Ref, s3Vis]   = useReveal(160);
  const { t } = useTranslation();

  return (
    <section id="about" className="ab-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes abSlideUp  { from{opacity:0;transform:translateY(48px)} to{opacity:1;transform:translateY(0)} }
        @keyframes abSlideIn  { from{opacity:0;transform:translateX(56px)} to{opacity:1;transform:translateX(0)} }
        @keyframes abBadge    { 0%,100%{transform:translateY(0) rotate(-2deg)} 50%{transform:translateY(-8px) rotate(-2deg)} }

        .ab-root {
          font-family: 'Outfit', sans-serif;
          background: #fff;
          position: relative; overflow: hidden;
          padding: 100px 0 80px;
        }

        /* Faint teal mesh background */
        .ab-mesh {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            radial-gradient(ellipse 80% 50% at 10% 40%, rgba(58,143,163,0.05) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 90% 70%, rgba(90,171,154,0.05) 0%, transparent 60%);
        }

        .ab-inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

        /* ── TOP ROW: eyebrow + title left, big image right ── */
        .ab-top {
          display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center;
          margin-bottom: 80px;
        }
        @media(max-width:900px){ .ab-top{grid-template-columns:1fr;} }

        .ab-top-left {
          opacity: 0; transform: translateY(48px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1);
        }
        .ab-top-left.in { opacity: 1; transform: translateY(0); }

        .ab-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #3a8fa3; background: rgba(58,143,163,0.08);
          border: 1px solid rgba(58,143,163,0.25);
          padding: 5px 16px; border-radius: 999px; margin-bottom: 22px;
        }
        .ab-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 800;
          color: #1a3a4a; line-height: 1.2; margin: 0 0 22px;
        }
        .ab-title .line { color: #3a8fa3; }
        .ab-desc {
          font-size: 1rem; color: #475569; line-height: 1.8; max-width: 460px; text-align: justify;
        }

        /* Image block: tall, with rotated badge */
        .ab-top-right {
          position: relative;
          opacity: 0; transform: translateX(56px);
          transition: all 0.85s cubic-bezier(0.4,0,0.2,1) 0.18s;
        }
        .ab-top-right.in { opacity: 1; transform: translateX(0); }

        .ab-img-outer {
          border-radius: 28px; overflow: hidden;
          box-shadow: 0 32px 72px rgba(58,143,163,0.16), 0 8px 24px rgba(0,0,0,0.08);
          transition: transform 0.45s cubic-bezier(0.4,0,0.2,1);
        }
        .ab-img-outer:hover { transform: translateY(-8px); }
        .ab-img-outer img { width: 100%; height: 380px; object-fit: cover; display: block; }

        /* Teal bottom strip on image */
        .ab-img-strip {
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          padding: 16px 24px;
          display: flex; align-items: center; gap: 10px;
        }
        .ab-img-strip-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(255,255,255,0.5); }
        .ab-img-strip-text {
          font-size: 0.78rem; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: rgba(255,255,255,0.9);
        }

        /* Floating rotated badge */
        .ab-badge {
          position: absolute; top: -18px; left: -18px;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(58,143,163,0.2);
          border-radius: 18px; padding: 16px 20px;
          display: flex; align-items: center; gap: 12px;
          box-shadow: 0 12px 36px rgba(58,143,163,0.2);
          transform: rotate(-2deg);
          animation: abBadge 5s ease-in-out infinite;
        }
        .ab-badge-icon {
          width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          display: flex; align-items: center; justify-content: center; font-size: 1.2rem;
          box-shadow: 0 4px 14px rgba(58,143,163,0.4);
        }
        .ab-badge-big   { font-size: 0.92rem; font-weight: 700; color: #1a3a4a; }
        .ab-badge-small { font-size: 0.72rem; color: #64748b; margin-top: 2px; }

        /* ── STATS ROW: 3 horizontal cards spanning full width ── */
        .ab-stats-row {
          display: grid; grid-template-columns: repeat(3,1fr); gap: 20px;
        }
        @media(max-width:700px){ .ab-stats-row{grid-template-columns:1fr;} }

        .ab-stat-card {
          position: relative; overflow: hidden;
          border-radius: 20px; padding: 32px 28px;
          background: linear-gradient(135deg, #f0f9fc 0%, #e4f4f7 100%);
          border: 1px solid rgba(58,143,163,0.12);
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          opacity: 0; transform: translateY(32px);
          transition-property: opacity, transform, box-shadow, border-color;
          transition-duration: 0.7s, 0.7s, 0.35s, 0.35s;
          transition-timing-function: cubic-bezier(0.4,0,0.2,1);
        }
        .ab-stat-card.in { opacity: 1; transform: translateY(0); }
        .ab-stat-card:hover {
          border-color: rgba(58,143,163,0.3);
          box-shadow: 0 12px 36px rgba(58,143,163,0.14);
          transform: translateY(-6px) !important;
        }

        /* Gradient accent top-left corner */
        .ab-stat-card::before {
          content: ''; position: absolute;
          top: 0; left: 0; width: 56px; height: 56px;
          background: linear-gradient(135deg, rgba(58,143,163,0.15), transparent);
          border-radius: 0 0 100% 0;
        }
        /* Large faint number in background */
        .ab-stat-card::after {
          content: attr(data-val);
          position: absolute; right: 16px; bottom: -8px;
          font-family: 'Playfair Display', serif; font-size: 5rem; font-weight: 800;
          color: rgba(58,143,163,0.07); line-height: 1;
          pointer-events: none; user-select: none;
        }

        .ab-stat-val {
          font-family: 'Playfair Display', serif;
          font-size: 2.6rem; font-weight: 800; color: #3a8fa3;
          line-height: 1; margin-bottom: 8px; position: relative; z-index: 1;
        }
        .ab-stat-lbl {
          font-size: 0.8rem; font-weight: 500; color: #5a7d8a;
          letter-spacing: 0.05em; position: relative; z-index: 1;
        }
      `}</style>

      <div className="ab-mesh" />
      <div className="ab-inner">

        {/* TOP: text + image */}
        <div className="ab-top">
          <div ref={secRef} className={`ab-top-left ${secVis ? 'in' : ''}`}>
            <div className="ab-eyebrow">{t("about.subtitle")}</div>
            <h2 className="ab-title">{t("about.title")}</h2>
            <p className="ab-desc">{t("about.description")}</p>
          </div>
          <div ref={imgRef} className={`ab-top-right ${imgVis ? 'in' : ''}`}>
            <div className="ab-badge">
              <div className="ab-badge-icon">🏆</div>
              <div>
                <div className="ab-badge-big">{t("about.certified")}</div>
                <div className="ab-badge-small">{t("about.standard")}</div>
              </div>
            </div>
            <div className="ab-img-outer">
              <img src={scopeImage} alt="Who We Are" />
              <div className="ab-img-strip">
                <div className="ab-img-strip-dot" />
                <div className="ab-img-strip-dot" />
                <span className="ab-img-strip-text">{t("about.subtitle")}</span>
              </div>
            </div>
          </div>
        </div>

        {/* STATS ROW */}
        {/* <div className="ab-stats-row">
          {[
            [s1Ref, s1Vis, '15+', t("about.yearsexperience"), 0],
            [s2Ref, s2Vis, '50+', t("about.countriesserved"), 80],
            [s3Ref, s3Vis, '100%', t("about.qualitycertified"), 160],
          ].map(([ref, vis, val, lbl, delay], i) => (
            <div
              key={i} ref={ref}
              className={`ab-stat-card ${vis ? 'in' : ''}`}
              data-val={val}
              style={{ transitionDelay: `${delay}ms` }}
            >
              <div className="ab-stat-val">{val}</div>
              <div className="ab-stat-lbl">{lbl}</div>
            </div>
          ))}
        </div> */}
      </div>
    </section>
  );
};

export default About;